import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SeoHead } from "@/components/SeoHead";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  FEATURE_PAGES_BY_SLUG,
  featurePagePath,
  type FeaturePage,
} from "@/lib/featurePages";
import { NotifyMeForm } from "@/components/marketing/NotifyMeForm";

type Props = { page: FeaturePage };

/**
 * Shared layout for every /features/:slug/ page. Reads everything from the
 * FeaturePage registry so SEO, content, and cross-links stay consistent.
 *
 * No hardcoded colors — all surfaces use semantic tokens (background/
 * foreground/primary/muted) so light + dark theme both work cleanly.
 */
const BASE_URL = "https://excerptus.io";

export const FeaturePageLayout = ({ page }: Props) => {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Features", item: `${BASE_URL}/#features` },
      {
        "@type": "ListItem",
        position: 3,
        name: page.navLabel,
        item: `${BASE_URL}${featurePagePath(page.slug)}`,
      },
    ],
  };

  const crossLinks = page.crossLinks
    .map((slug) => FEATURE_PAGES_BY_SLUG[slug])
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SeoHead title={page.title} description={page.description} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <MarketingNav />

      {/* Hero */}
      <section className="mx-auto max-w-4xl px-4 pt-12 pb-10 text-center sm:px-6 sm:pt-20 sm:pb-16">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
          {page.eyebrow}
        </p>
        <h1 className="mt-3 font-serif text-4xl font-semibold leading-tight sm:text-5xl whitespace-pre-line">
          {page.h1}
        </h1>
        {page.badge && (
          <div className="mt-4 flex justify-center">
            <Badge variant="secondary" className="font-normal">{page.badge}</Badge>
          </div>
        )}
        <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">
          {page.sub}
        </p>

        {page.notifyForm ? (
          <div className="mx-auto mt-8 max-w-md">
            <NotifyMeForm
              source={page.notifyForm.source}
              helper={page.notifyForm.helper}
            />
          </div>
        ) : (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to={page.ctaHref ?? "/pricing/"}>
              <Button size="lg" className="min-w-44">
                {page.ctaLabel ?? "Start free trial"}
              </Button>
            </Link>
            {page.secondaryCta && (
              <Link to={page.secondaryCta.href}>
                <Button size="lg" variant="ghost" className="min-w-44">
                  {page.secondaryCta.label}
                </Button>
              </Link>
            )}
          </div>
        )}
      </section>

      {/* Feature blocks */}
      <section className="mx-auto max-w-6xl px-4 pb-12 sm:px-6 sm:pb-16">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {page.blocks.map(({ icon: Icon, title, body, wide }) => (
            <article
              key={title}
              className={`rounded-xl border border-border bg-card p-6 shadow-soft${
                wide ? " sm:col-span-2 lg:col-span-3" : ""
              }`}
            >
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon className="h-5 w-5" aria-hidden />
              </div>
              <h3 className="mt-4 font-serif text-lg font-semibold">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Optional callout */}
      {page.callout && (
        <section className="mx-auto max-w-4xl px-4 pb-12 sm:px-6 sm:pb-16">
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-6 text-center sm:p-8">
            <h2 className="font-serif text-2xl font-semibold">{page.callout.title}</h2>
            <p className="mx-auto mt-2 max-w-2xl text-muted-foreground">{page.callout.body}</p>
            {page.callout.href && page.callout.linkLabel && (
              <Link
                to={page.callout.href}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              >
                {page.callout.linkLabel}
              </Link>
            )}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
        <h2 className="mb-6 text-center font-serif text-3xl font-semibold">Questions</h2>
        <Accordion type="single" collapsible className="w-full">
          {page.faq.map((item, idx) => (
            <AccordionItem key={item.q} value={`faq-${idx}`}>
              <AccordionTrigger className="text-left font-medium">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Cross-links */}
      {crossLinks.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 pb-12 sm:px-6 sm:pb-16">
          <h2 className="mb-5 font-serif text-2xl font-semibold">Keep Exploring</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {crossLinks.map((cl) => (
              <Link
                key={cl.slug}
                to={featurePagePath(cl.slug)}
                className="group rounded-xl border border-border bg-card p-5 shadow-soft transition-colors hover:border-primary/40"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-serif text-lg font-semibold">{cl.navLabel}</span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{cl.navDescription}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Final CTA (omitted on notify-only pages) */}
      {!page.notifyForm && (
        <section className="border-t border-border bg-muted">
          <div className="mx-auto max-w-3xl px-4 py-14 text-center sm:px-6 sm:py-20">
            <h2 className="font-serif text-3xl font-semibold sm:text-4xl">
              Give Your Second Brain a Heart
            </h2>
            <p className="mt-3 text-muted-foreground">
              10-day free trial on every plan.
            </p>
            <Link to="/pricing/" className="mt-8 inline-block">
              <Button size="lg" className="min-w-44">Start free trial</Button>
            </Link>
          </div>
        </section>
      )}

      <MarketingFooter />
    </div>
  );
};
